import { router } from '@inertiajs/vue3'
import { onBeforeUnmount, ref } from 'vue'

/**
 * C5 A (Arnold, 08.10.): which skeleton a planner page gets while its visit waits for the server.
 * Three types: KPI band and table (overview, budget, costs, sponsors, reporting), a table
 * (vendors, documents, the organisation's settings, budgets, payments and vendors), and the task
 * board — or the task list (`list`) when the visit asks for `?view=list`.
 */
const EVENT_PAGES = {
  '': 'kpi-table',
  budget: 'kpi-table',
  costs: 'kpi-table',
  sponsors: 'kpi-table',
  reporting: 'kpi-table',
  vendors: 'table',
  documents: 'table',
  tasks: 'board',
}

const ORG_PAGES = {
  '': 'table',
  budgets: 'table',
  payments: 'table',
  vendors: 'table',
  tasks: 'board',
}

/** The delay before a skeleton shows: quicker page changes do not flicker. */
export const SKELETON_DELAY = 150

/**
 * @param {URL} url
 * @returns {'kpi-table'|'table'|'board'|'list'|null} null for any page outside the planner
 */
export function plannerSkeletonFor(url) {
  const path = url.pathname.replace(/\/+$/, '')
  const match =
    path.match(/^\/events\/[^/]+\/planner(?:\/([a-z-]+))?$/) ??
    path.match(/^\/companies\/planning(?:\/([a-z-]+))?$/)

  if (match === null) {
    return null
  }

  const pages = path.startsWith('/events/') ? EVENT_PAGES : ORG_PAGES
  const type = pages[match[1] ?? ''] ?? null

  if (type === 'board' && url.searchParams.get('view') === 'list') {
    return 'list'
  }

  return type
}

/**
 * The skeleton type to show now: set 150 ms after an Inertia visit to another planner page
 * starts, cleared when it finishes (also when it fails or is cancelled). Visits that stay on the
 * page (filters, the board/list switch, saving, partial reloads) never show one.
 */
export function usePlannerSkeleton() {
  const skeleton = ref(null)
  let timer = null

  const clear = () => {
    clearTimeout(timer)
    timer = null
    skeleton.value = null
  }

  const stopStart = router.on('start', (event) => {
    const visit = event.detail.visit
    const url = new URL(visit.url, window.location.origin)

    clear()

    if (
      visit.prefetch ||
      String(visit.method).toLowerCase() !== 'get' ||
      visit.only?.length > 0 ||
      visit.except?.length > 0 ||
      url.pathname === window.location.pathname
    ) {
      return
    }

    const type = plannerSkeletonFor(url)

    if (type !== null) {
      timer = setTimeout(() => (skeleton.value = type), SKELETON_DELAY)
    }
  })

  const stopFinish = router.on('finish', clear)

  onBeforeUnmount(() => {
    stopStart()
    stopFinish()
    clear()
  })

  return skeleton
}

/**
 * Whether an Inertia page component belongs to the planner (SectionTabs leaves those pages to
 * the skeleton instead of its loading cover).
 *
 * @param {string|null} component
 */
export const isPlannerComponent = (component) =>
  typeof component === 'string' && component.startsWith('Planner/')
